import { useProcessNames } from '@hooks/useProcessNames';
import { useTranslation } from '@hooks/useTranslation';
import { Process } from '@models/process.model';
import { StepExecution, StepExecutionStatus } from '@models/processExecution.model';
import { ConfirmationType, Step, StepType } from '@models/step.model';
import { UserRoles } from '@models/user.model';
import { ArrowBack, CheckCircleOutline, ErrorOutline, PendingOutlined, RedoOutlined } from '@mui/icons-material';
import { Box, Button, Dialog, Fade, Icon, Typography } from '@mui/material';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import enhanceHTML from '@utils/enhanceHtml';
import { filterStepsInsideSwitchRepeat } from '@utils/filterStepsInsideSwitchRepeat';
import { getStepsInsideSwitchRepeat } from '@utils/getStepsInsideSwitchRepeat';
import sanitizeHTML from '@utils/sanitize-html';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import WolfButton from './ui/WolfButton';
import { EditIcon24, OutboundIcon24 } from './ui/WolfIcons';
import WolfStepIndicator from './ui/WolfStepIndicator';
import WolfTextarea from './ui/WolfTextarea';
import WolfTooltip from './ui/WolfTooltip';

interface ProcessContentStaticProps {
  isGenerating?: boolean;
  process: Process | undefined;
  auxStep: Step | undefined;
  delay: number;
  stepsRefs?: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
  handleSelectStep: (stepId: string, fromScroll: boolean) => void;
  handleEditStep: (stepId: string) => void;
  isExecuting?: boolean;
  stepExecutions?: any[];
  currentStepIndex?: number;
  onMarkStep?: (stepIndex: number, status: any, notes?: string, confirmationResponse?: string) => void;
  onGoBack?: () => void;
  isGoingBack?: boolean;
  hasVersions: boolean;
  // Substep execution props - keyed by parentStepId (step definition ID)
  substepExecutions?: Map<string, StepExecution[]>;
  currentSubstepIndices?: Map<string, number>;
  repeatIterations?: Map<string, number>;
  completedIterations?: Map<string, number[]>;
  showIterationCompleteFor?: string | null;
  onMarkSubstep?: (parentStepId: string, substepIndex: number, status: StepExecutionStatus, notes?: string, confirmationResponse?: string) => void;
  onRepeatAddAnother?: (parentStepId: string) => void;
  onRepeatDone?: (parentStepId: string) => void;
  executionNote?: string | null;
}

const ProcessContentStatic: React.FC<ProcessContentStaticProps> = ({
  isGenerating,
  process,
  auxStep,
  stepsRefs,
  handleSelectStep,
  handleEditStep,
  delay,
  isExecuting = false,
  stepExecutions = [],
  currentStepIndex = 0,
  onMarkStep,
  onGoBack,
  isGoingBack = false,
  hasVersions = false,
  // Substep execution props - keyed by parentStepId (step definition ID)
  substepExecutions = new Map(),
  currentSubstepIndices = new Map(),
  repeatIterations = new Map(),
  completedIterations = new Map(),
  showIterationCompleteFor = null,
  onMarkSubstep,
  onRepeatAddAnother,
  onRepeatDone,
  executionNote,
}) => {
  const localeProcesses = useTranslation('processes');
  const localeCommon = useTranslation('common');

  const { role } = useAppSelector((state: RootState) => state.user);
  const history = useHistory();

  const [hoveredStep, setHoveredStep] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [imgSrc, setImgSrc] = useState('');
  const [executionNotes, setExecutionNotes] = useState<string>('');
  const [confirmationResponse, setConfirmationResponse] = useState<string>('');

  // Substep execution state
  const [substepNotes, setSubstepNotes] = useState<Map<string, string>>(new Map());

  const [enhancedHtmlMap, setEnhancedHtmlMap] = useState<{ [key: string]: { __html: string } }>({});
  // Track which step descriptions we've already enhanced or are currently enhancing (ref to avoid race conditions)
  const enhancedOrPendingStepIdsRef = useRef<Set<string>>(new Set());
  // Track the current process ID to reset cache when process changes
  const lastProcessIdRef = useRef<string | undefined>(undefined);

  const handleScroll = (event: any) => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const offsetY = -50;
    const viewportHeight = window.innerHeight + offsetY;
    if (stepsRefs && stepsRefs?.current) {
      let matched = false;
      Object.keys(stepsRefs.current).forEach((key, index) => {
        const section = stepsRefs.current[key];
        if (section) {
          const { offsetTop, offsetHeight } = section;
          if (offsetTop < scrollTop + viewportHeight && offsetTop + offsetHeight > scrollTop && !matched) {
            handleSelectStep(key, true);
            matched = true;
          }
        }
      });
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Extract process IDs for the hook
  const processIds = useMemo(() => {
    return process?.steps
      ?.map((step) => step?.stepData?.executeProcessId)
      .filter(Boolean) || [];
  }, [process?.steps]);

  // Use the optimized hook
  const { getProcessNameById } = useProcessNames(processIds);

  useEffect(() => {
    const fetchAndEnhanceHTML = async () => {
      if (!process?.steps || process.steps.length === 0) return;

      // If process changed, reset the cache
      const currentProcessId = process?.processId;

      if (currentProcessId !== lastProcessIdRef.current) {
        lastProcessIdRef.current = currentProcessId;
        setEnhancedHtmlMap({});
        enhancedOrPendingStepIdsRef.current = new Set();
      }

      // Only process steps that haven't been enhanced or are not currently being enhanced
      const stepsToEnhance = process.steps.filter(
        step => step?.stepData?.description && !enhancedOrPendingStepIdsRef.current.has(step.stepId)
      );

      if (stepsToEnhance.length === 0) return;

      // Mark steps as pending immediately (synchronously) to prevent duplicate requests
      stepsToEnhance.forEach(step => enhancedOrPendingStepIdsRef.current.add(step.stepId));

      const newEnhancedHtmls: { [key: string]: { __html: string } } = {};

      for (const step of stepsToEnhance) {
        const sanitizedHtml = sanitizeHTML(step?.stepData?.description);
        const enhanced = await enhanceHTML(sanitizedHtml);
        newEnhancedHtmls[step.stepId] = enhanced;
      }

      // Merge with existing enhanced HTML
      setEnhancedHtmlMap(prev => ({ ...prev, ...newEnhancedHtmls }));
    };

    fetchAndEnhanceHTML();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [process?.processId, process?.steps]);

  // Auto-scroll to current step during execution
  useEffect(() => {
    if (isExecuting && stepsRefs?.current && process?.steps) {
      const filteredSteps = filterStepsInsideSwitchRepeat(process.steps);
      const currentStep = filteredSteps?.[currentStepIndex];
      if (currentStep && stepsRefs.current[currentStep.stepId]) {
        stepsRefs.current[currentStep.stepId]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }
  }, [isExecuting, currentStepIndex, process?.steps, stepsRefs]);

  const stepDescriptionClick = (event: any) => {
    // We check that the event target is valid and that is not the placeholder image for files or broken links
    if (
      event.target.tagName === 'IMG' &&
      event.target.src &&
      event.target.alt !== 'FILE' &&
      event.target.alt !== 'BROKEN'
    ) {
      setImgSrc(event.target.src);
      setShowImageModal(true);
    }
  };


  const closeModal = () => {
    setShowImageModal(false);
    setImgSrc('');
  };

  const stepList = filterStepsInsideSwitchRepeat(process?.steps)?.map(
    (step, index) =>
      stepsRefs && (
        <Box
          flexGrow={1}
          gap={2}
          className={`my-2 w-80per p-4 px-8 bg-white pr-8 rounded-lg transition-all duration-300 ${delay ? 'animate-fadeIn' : ''} ${
            isExecuting && index === currentStepIndex
              ? 'border-2 border-solid border-callToAction animate-pulseGlow shadow-shadow3modals'
              : isExecuting
                ? 'border border-solid border-gray-200'
                : ''
          }`}
          key={step.stepId}
          ref={(el: HTMLDivElement) => (stepsRefs.current[step?.stepId] = el)}
          sx={{ scrollMargin: 100 }}>
          {/* Current Step Badge */}
          {isExecuting && index === currentStepIndex && (
            <Box className="flex items-center gap-2 mb-2">
              <Box className="flex items-center gap-1 px-3 py-1 bg-callToAction text-white rounded-full">
                <Box className="w-2 h-2 bg-white rounded-full animate-pulse" />
                <Typography variant="body12semibold" className="text-white">
                  {localeProcesses['currentStep']}
                </Typography>
              </Box>
            </Box>
          )}
          <Box
            className="flex gap-2 items-center relative bg-white"
            onMouseEnter={() => setHoveredStep(step.stepId)}
            onMouseLeave={() => setHoveredStep(null)}>
            <WolfStepIndicator text={(index + 1).toString()} type={step.stepType} isSub={false} />
            <Typography variant="h4semibold" className="mb-6 w-fit">
              {step?.name}
            </Typography>
            {!isGenerating && role !== UserRoles.USER && hoveredStep === step.stepId && (
              <Icon onClick={() => handleEditStep(step.stepId)} className="p-0 text-sm absolute right-1">
                <EditIcon24 />
              </Icon>
            )}
          </Box>

          <Box className="flex flex-wrap justify-between gap-4 mt-4">
            {step?.notes && (
              <Box className="p-4 w-full border-2 rounded-lg border-solid border-utilityLemon">
                <Typography variant="body12semibold" className="block w-full !mb-2">
                  {localeProcesses['notes']}
                </Typography>
                <Typography variant="body14">{step?.notes ? step.notes : localeProcesses['noNotes']}</Typography>
              </Box>
            )}

            {step?.stepData?.condition && (
              <Box className="p-4 rounded-lg border-2 border-solid border-feedbackError w-full">
                <Typography variant="body12semibold" className="block w-full !mb-2">
                  {localeProcesses['condition']}
                </Typography>
                <Typography variant="body14">
                  {step?.stepData?.condition ? step.stepData.condition : localeProcesses['noCondition']}
                </Typography>
              </Box>
            )}
          </Box>
          {step?.stepData?.description && (
            <div
              onClick={stepDescriptionClick}
              className="reset-base-styles"
              dangerouslySetInnerHTML={enhancedHtmlMap[step?.stepId]}
            />
          )}
          {/* Substep rendering for SWITCH/REPEAT */}
          {stepsRefs && [StepType.REPEAT, StepType.SWITCH].includes(step.stepType) && (() => {
            // Use step.stepId as the key (container steps don't have their own executions)
            const parentStepId = step.stepId;
            const isCurrentMainStep = index === currentStepIndex;
            const substepsFromProcess = getStepsInsideSwitchRepeat(step?.stepData?.stepsIds, process?.steps);
            const allSubstepExecsForStep = substepExecutions.get(parentStepId) || [];

            // During execution mode - show completed summary if we've moved past this step
            if (isExecuting && !isCurrentMainStep && allSubstepExecsForStep.length > 0) {
              // Check if all substeps are completed
              const allComplete = allSubstepExecsForStep.every((s: StepExecution) =>
                s.status === StepExecutionStatus.COMPLETED ||
                s.status === StepExecutionStatus.SKIPPED ||
                s.status === StepExecutionStatus.FAILED
              );

              if (allComplete) {
                if (step.stepType === StepType.REPEAT) {
                  // Group by iteration and show summary
                  const iterationNumbers = Array.from(new Set<number>(allSubstepExecsForStep.map((s: StepExecution) => s.iterationNumber || 1))).sort((a, b) => a - b);
                  return (
                    <Box className="ml-8 mt-4">
                      {iterationNumbers.map((iterNum: number) => {
                        const iterSubsteps = allSubstepExecsForStep.filter((s: StepExecution) => s.iterationNumber === iterNum);
                        const completedCount = iterSubsteps.filter((s: StepExecution) => s.status === StepExecutionStatus.COMPLETED).length;
                        const failedCount = iterSubsteps.filter((s: StepExecution) => s.status === StepExecutionStatus.FAILED).length;
                        const substepsWithNotes = iterSubsteps.filter((s: StepExecution) => s.notes);
                        return (
                          <Box key={iterNum} className="p-3 bg-gray-100 rounded-lg mb-2">
                            <Box className="flex items-center gap-2">
                              {failedCount > 0 ? (
                                <ErrorOutline sx={{ color: 'error.main', fontSize: 18 }} />
                              ) : (
                                <CheckCircleOutline sx={{ color: 'success.main', fontSize: 18 }} />
                              )}
                              <Typography variant="body14semibold">
                                {localeProcesses['iteration']} {iterNum}: {completedCount}/{iterSubsteps.length} {localeProcesses['completed']}
                              </Typography>
                            </Box>
                            {substepsWithNotes.length > 0 && (
                              <Box className="mt-2 pl-6 border-l-2 border-utilityLemon">
                                {substepsWithNotes.map((substep: StepExecution) => (
                                  <Box key={substep.stepExecutionId} className="py-1">
                                    <Typography variant="body12" className="text-gray-600">
                                      <span className="font-semibold">{substep.stepSnapshot?.name}:</span> {substep.notes}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  );
                } else {
                  // SWITCH: show simple summary
                  const completedCount = allSubstepExecsForStep.filter((s: StepExecution) => s.status === StepExecutionStatus.COMPLETED).length;
                  const failedCount = allSubstepExecsForStep.filter((s: StepExecution) => s.status === StepExecutionStatus.FAILED).length;
                  return (
                    <Box className="ml-8 mt-4 p-3 bg-gray-100 rounded-lg flex items-center gap-2">
                      {failedCount > 0 ? (
                        <ErrorOutline sx={{ color: 'error.main', fontSize: 18 }} />
                      ) : (
                        <CheckCircleOutline sx={{ color: 'success.main', fontSize: 18 }} />
                      )}
                      <Typography variant="body14semibold">
                        {completedCount}/{allSubstepExecsForStep.length} {localeProcesses['completed']}
                      </Typography>
                    </Box>
                  );
                }
              }
            }

            // During execution mode with substeps loaded - current step
            if (isExecuting && isCurrentMainStep && allSubstepExecsForStep.length > 0) {
              const allSubstepExecs = allSubstepExecsForStep;
              const currentSubstepIdx = currentSubstepIndices.get(parentStepId) || 0;
              const currentIter = repeatIterations.get(parentStepId) || 1;
              const completedIters = completedIterations.get(parentStepId) || [];

              // For REPEAT: Show completed iterations collapsed, then current iteration
              if (step.stepType === StepType.REPEAT) {
                return (
                  <Box className="ml-8 mt-4">
                    {/* Collapsed completed iterations */}
                    {completedIters.map((iterNum: number) => {
                      const iterSubsteps = allSubstepExecs.filter((s: StepExecution) => s.iterationNumber === iterNum);
                      const completed = iterSubsteps.filter((s: StepExecution) => s.status === StepExecutionStatus.COMPLETED).length;
                      const substepsWithNotes = iterSubsteps.filter((s: StepExecution) => s.notes);
                      return (
                        <Box key={iterNum} className="p-3 bg-gray-100 rounded-lg mb-2">
                          <Box className="flex items-center gap-2">
                            <CheckCircleOutline sx={{ color: 'success.main', fontSize: 18 }} />
                            <Typography variant="body14semibold">
                              {localeProcesses['iteration']} {iterNum}: {completed}/{iterSubsteps.length} {localeProcesses['completed']}
                            </Typography>
                          </Box>
                          {substepsWithNotes.length > 0 && (
                            <Box className="mt-2 pl-6 border-l-2 border-utilityLemon">
                              {substepsWithNotes.map((substep: StepExecution) => (
                                <Box key={substep.stepExecutionId} className="py-1">
                                  <Typography variant="body12" className="text-gray-600">
                                    <span className="font-semibold">{substep.stepSnapshot?.name}:</span> {substep.notes}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      );
                    })}

                    {/* Current iteration */}
                    <Box className="border-l-4 border-utilityGreen pl-4">
                      <Typography variant="body12semibold" className="mb-2 text-utilityGreen">
                        {localeProcesses['iteration']} {currentIter}
                      </Typography>
                      {allSubstepExecs
                        .filter((s: StepExecution) => s.iterationNumber === currentIter)
                        .map((substepExec: StepExecution, idx: number) => {
                          const originalSubstep = substepsFromProcess.find(s => s.stepId === substepExec.stepId);
                          const isCurrentSubstep = idx === currentSubstepIdx && !showIterationCompleteFor;
                          const substepKey = substepExec.stepExecutionId;
                          return (
                            <Box
                              key={substepKey}
                              data-substep-parent={parentStepId}
                              data-substep-index={idx}
                              className={`p-3 rounded-lg mb-2 ${
                                isCurrentSubstep
                                  ? 'border-2 border-callToAction bg-ctaShades1'
                                  : substepExec.status === StepExecutionStatus.COMPLETED
                                    ? 'border border-feedbackSuccess bg-green-50'
                                    : substepExec.status === StepExecutionStatus.FAILED
                                      ? 'border border-feedbackError bg-red-50'
                                      : 'border border-gray-200 bg-gray-50'
                              }`}>
                              <Box className="flex items-center gap-2 mb-2">
                                <WolfStepIndicator text={`${index + 1}.${idx + 1}`} type={StepType.REPEAT} isSub={true} />
                                <Typography variant="body14semibold">{originalSubstep?.name || substepExec.stepSnapshot?.name}</Typography>
                                {!isCurrentSubstep && substepExec.status === StepExecutionStatus.COMPLETED && (
                                  <CheckCircleOutline sx={{ color: 'success.main', fontSize: 18, ml: 'auto' }} />
                                )}
                              </Box>
                              {/* Substep description */}
                              {(originalSubstep?.stepData?.description || substepExec.stepSnapshot?.stepData?.description) && (() => {
                                const substepIdKey = originalSubstep?.stepId || substepExec.stepId;
                                const descHtml = (substepIdKey && enhancedHtmlMap[substepIdKey]) || sanitizeHTML(originalSubstep?.stepData?.description || substepExec.stepSnapshot?.stepData?.description || '');
                                return (
                                  <div
                                    onClick={stepDescriptionClick}
                                    className="reset-base-styles text-sm mb-2"
                                    dangerouslySetInnerHTML={descHtml}
                                  />
                                );
                              })()}
                              {isCurrentSubstep && (
                                <Box className="mt-3">
                                  <WolfTextarea
                                    label=""
                                    value={substepNotes.get(substepKey) || ''}
                                    onChange={(e) => setSubstepNotes(prev => new Map(prev).set(substepKey, e.target.value))}
                                    customRows={1}
                                    fullWidth
                                    placeholder={localeProcesses['addNotes']}
                                  />
                                  <Box className="flex gap-2 mt-2 flex-wrap">
                                    <WolfButton size="small" variant="outlined" color="error"
                                      onClick={() => { onMarkSubstep?.(parentStepId, idx, StepExecutionStatus.FAILED, substepNotes.get(substepKey)); setSubstepNotes(prev => { const m = new Map(prev); m.delete(substepKey); return m; }); }}>
                                      {localeProcesses['markStepWithError']}
                                    </WolfButton>
                                    <WolfButton size="small" variant="outlined" color="secondary"
                                      onClick={() => { onMarkSubstep?.(parentStepId, idx, StepExecutionStatus.SKIPPED, substepNotes.get(substepKey)); setSubstepNotes(prev => { const m = new Map(prev); m.delete(substepKey); return m; }); }}>
                                      {localeProcesses['markStepNotDone']}
                                    </WolfButton>
                                    <WolfButton size="small" variant="contained" color="success"
                                      onClick={() => { onMarkSubstep?.(parentStepId, idx, StepExecutionStatus.COMPLETED, substepNotes.get(substepKey)); setSubstepNotes(prev => { const m = new Map(prev); m.delete(substepKey); return m; }); }}>
                                      {localeProcesses['markStepDone']}
                                    </WolfButton>
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                    </Box>

                    {/* Iteration complete buttons */}
                    {showIterationCompleteFor === parentStepId && (
                      <Box className="mt-4 flex justify-between">
                        <WolfButton onClick={() => onRepeatAddAnother?.(parentStepId)} variant="outlined" color="primary">
                          {localeProcesses['addAnotherIteration']}
                        </WolfButton>
                        <WolfButton onClick={() => onRepeatDone?.(parentStepId)} variant="contained" color="success">
                          {localeProcesses['doneNoMore']}
                        </WolfButton>
                      </Box>
                    )}
                  </Box>
                );
              }

              // For SWITCH: Show all substeps with execution controls
              if (step.stepType === StepType.SWITCH) {
                // Sort substeps by substepOrder (1-based, set by backend)
                const sortedSubsteps = [...allSubstepExecs].sort((a, b) =>
                  (a.substepOrder ?? 0) - (b.substepOrder ?? 0)
                );
                return (
                  <Box className="ml-8 mt-4 border-l-4 border-utilityYellow pl-4">
                    {sortedSubsteps.map((substepExec: StepExecution, idx: number) => {
                      const originalSubstep = substepsFromProcess.find(s => s.stepId === substepExec.stepId);
                      const isCurrentSubstep = idx === currentSubstepIdx;
                      const substepKey = substepExec.stepExecutionId;
                      return (
                        <Box
                          key={substepKey}
                          data-substep-parent={parentStepId}
                          data-substep-index={idx}
                          className={`p-3 rounded-lg mb-2 ${
                            isCurrentSubstep
                              ? 'border-2 border-callToAction bg-ctaShades1'
                              : substepExec.status === StepExecutionStatus.COMPLETED
                                ? 'border border-feedbackSuccess bg-green-50'
                                : substepExec.status === StepExecutionStatus.FAILED
                                  ? 'border border-feedbackError bg-red-50'
                                  : 'border border-gray-200 bg-gray-50'
                          }`}>
                          <Box className="flex items-center gap-2 mb-2">
                            <WolfStepIndicator text={`${index + 1}.${String.fromCharCode(idx + 65)}`} type={StepType.SWITCH} isSub={true} />
                            <Typography variant="body14semibold">{originalSubstep?.name || substepExec.stepSnapshot?.name}</Typography>
                            {!isCurrentSubstep && substepExec.status === StepExecutionStatus.COMPLETED && (
                              <CheckCircleOutline sx={{ color: 'success.main', fontSize: 18, ml: 'auto' }} />
                            )}
                          </Box>
                          {/* Substep description */}
                          {(originalSubstep?.stepData?.description || substepExec.stepSnapshot?.stepData?.description) && (() => {
                            const substepIdKey = originalSubstep?.stepId || substepExec.stepId;
                            const descHtml = (substepIdKey && enhancedHtmlMap[substepIdKey]) || sanitizeHTML(originalSubstep?.stepData?.description || substepExec.stepSnapshot?.stepData?.description || '');
                            return (
                              <div
                                onClick={stepDescriptionClick}
                                className="reset-base-styles text-sm mb-2"
                                dangerouslySetInnerHTML={descHtml}
                              />
                            );
                          })()}
                          {isCurrentSubstep && (
                            <Box className="mt-3">
                              <WolfTextarea
                                label=""
                                value={substepNotes.get(substepKey) || ''}
                                onChange={(e) => setSubstepNotes(prev => new Map(prev).set(substepKey, e.target.value))}
                                customRows={1}
                                fullWidth
                                placeholder={localeProcesses['addNotes']}
                              />
                              <Box className="flex gap-2 mt-2 flex-wrap">
                                <WolfButton size="small" variant="outlined" color="error"
                                  onClick={() => { onMarkSubstep?.(parentStepId, idx, StepExecutionStatus.FAILED, substepNotes.get(substepKey)); setSubstepNotes(prev => { const m = new Map(prev); m.delete(substepKey); return m; }); }}>
                                  {localeProcesses['markStepWithError']}
                                </WolfButton>
                                <WolfButton size="small" variant="outlined" color="secondary"
                                  onClick={() => { onMarkSubstep?.(parentStepId, idx, StepExecutionStatus.SKIPPED, substepNotes.get(substepKey)); setSubstepNotes(prev => { const m = new Map(prev); m.delete(substepKey); return m; }); }}>
                                  {localeProcesses['markStepNotDone']}
                                </WolfButton>
                                <WolfButton size="small" variant="contained" color="success"
                                  onClick={() => { onMarkSubstep?.(parentStepId, idx, StepExecutionStatus.COMPLETED, substepNotes.get(substepKey)); setSubstepNotes(prev => { const m = new Map(prev); m.delete(substepKey); return m; }); }}>
                                  {localeProcesses['markStepDone']}
                                </WolfButton>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                );
              }
            }

            // Non-execution mode or substeps not loaded: Show static substep list
            return substepsFromProcess.map((substep, idx) => (
              <Box
                className="ml-8"
                key={substep.stepId}
                ref={(el: HTMLDivElement) => (stepsRefs.current[substep?.stepId] = el)}
                sx={{ scrollMargin: 100 }}>
                <Box
                  className="flex gap-2 items-center relative"
                  onMouseEnter={() => setHoveredStep(substep.stepId)}
                  onMouseLeave={() => setHoveredStep(null)}>
                  <WolfStepIndicator
                    text={step.stepType === StepType.SWITCH ? index + 1 + '.' + String.fromCharCode(idx + 65) : index + 1 + '.' + (idx + 1)}
                    type={step.stepType}
                    isSub={true}
                  />
                  <Typography variant="h4semibold" className="!my-2">{substep?.name}</Typography>
                  {!isGenerating && role !== UserRoles.USER && hoveredStep === substep.stepId && (
                    <Icon onClick={() => handleEditStep(substep.stepId)} className="p-0 text-sm absolute right-1 cursor-pointer">
                      <EditIcon24 />
                    </Icon>
                  )}
                </Box>
                <Box className="flex flex-wrap justify-between gap-4 mt-4">
                  {substep?.notes && (
                    <Box className="p-4 w-full border-2 rounded-lg border-solid border-utilityLemon">
                      <Typography variant="body12semibold" className="block w-full !mb-2">{localeProcesses['notes']}</Typography>
                      <Typography variant="body14">{substep.notes}</Typography>
                    </Box>
                  )}
                  {substep?.stepData?.condition && (
                    <Box className="p-4 rounded-lg border-2 border-solid border-feedbackError w-full">
                      <Typography variant="body12semibold" className="block w-full !mb-2">{localeProcesses['condition']}</Typography>
                      <Typography variant="body14">{substep.stepData.condition}</Typography>
                    </Box>
                  )}
                </Box>
                {substep?.stepData?.description && (
                  <div onClick={stepDescriptionClick} className="reset-base-styles" dangerouslySetInnerHTML={enhancedHtmlMap[substep?.stepId]} />
                )}
                {substep?.stepData?.executeProcessId && (
                  <Box className="flex flex-wrap items-start justify-center p-4 mx-auto cursor-pointer">
                    <Box className="flex items-center justify-center w-full">
                      <Typography variant="body14" className="text-additionalPlaceholder">{localeProcesses['linkedToProcess']}</Typography>
                    </Box>
                    <Box className="flex justify-center items-center border-2 border-solid border-frozenGrayShades2 rounded-md px-4 py-2"
                      onClick={() => history.push(`/processes/${substep?.stepData?.executeProcessId}`)}>
                      <Typography variant="body14semibold" className="!mr-2">{getProcessNameById(substep?.stepData?.executeProcessId) || localeProcesses['noLinked']}</Typography>
                      <Icon><OutboundIcon24 /></Icon>
                    </Box>
                  </Box>
                )}
              </Box>
            ));
          })()}
          {step?.stepData?.executeProcessId && (
            <Box className="flex flex-wrap items-start justify-center p-4 mx-auto">
              <Box className="flex items-center justify-center w-full">
                <Typography variant="body14" className="text-additionalPlaceholder">
                  {localeProcesses['linkedToProcess']}
                </Typography>
              </Box>
              <WolfTooltip
                title={isExecuting ? localeProcesses['linkedProcessOpensInNewTab'] : ''}
                disableHoverListener={!isExecuting}>
                <Box
                  className="flex justify-center items-center border-2 border-solid border-frozenGrayShades2 rounded-md px-4 py-2 cursor-pointer"
                  onClick={(event) => {
                    const openInNewTab = isExecuting || event.metaKey || event.ctrlKey;
                    if (openInNewTab) {
                      window.open(`/processes/${step?.stepData?.executeProcessId}`, '_blank');
                    } else {
                      history.push(`/processes/${step?.stepData?.executeProcessId}`);
                    }
                  }}>
                  <Typography variant="body14semibold" className="!mr-2">
                    {getProcessNameById(step?.stepData?.executeProcessId) || localeProcesses['noLinked']}
                  </Typography>
                  <Icon>
                    <OutboundIcon24 />
                  </Icon>
                </Box>
              </WolfTooltip>
            </Box>
          )}

          {/* Inline Execution Controls - skip for container steps (SWITCH/REPEAT) which use substep UI */}
          {isExecuting && step.stepType !== StepType.SWITCH && step.stepType !== StepType.REPEAT && (() => {
            // Look up step execution by stepId, not by array index
            const stepExecution = stepExecutions.find(se => se.stepId === step.stepId);
            const isCurrentStep = index === currentStepIndex;
            const stepStatus = stepExecution?.status;

            return (
            <Box className={`mt-4 p-4 rounded-lg border-2 ${
              isCurrentStep
                ? 'border-callToAction bg-ctaShades1'
                : stepStatus === StepExecutionStatus.COMPLETED
                  ? 'border-feedbackSuccess bg-green-50'
                  : stepStatus === StepExecutionStatus.FAILED
                    ? 'border-feedbackError bg-red-50'
                    : stepStatus === StepExecutionStatus.SKIPPED
                      ? 'border-warning bg-yellow-50'
                      : 'border-gray-300 bg-gray-50'
            }`}>
              {isCurrentStep && (
                <Box className="flex flex-col gap-3">
                  {/* Execution Notes */}
                  <Box className="flex flex-col gap-1">
                    <Typography variant="body14semibold">
                      {localeProcesses['yourExecutionNotes']}
                    </Typography>
                    <Typography variant="body12" className="text-gray-500 mb-1">
                      {localeProcesses['addNotes']}
                    </Typography>
                    <WolfTextarea
                      label=""
                      value={executionNotes}
                      onChange={(e) => setExecutionNotes(e.target.value)}
                      customRows={2}
                      fullWidth
                    />
                  </Box>

                  {/* Confirmation UI if required */}
                  {step?.stepData?.confirmationType && step.stepData.confirmationType !== ConfirmationType.NONE && (
                    <Box className="border-2 border-solid border-utilityOrange rounded-lg p-3 bg-utilityLemonShades1">
                      <Box className="flex items-center gap-2 mb-2">
                        <Typography variant="body12semibold" className="text-utilityOrange">
                        ⚠️ {step.stepData.confirmationType === ConfirmationType.BOOLEAN ? localeProcesses['confirmationRequired'] : step.stepData.confirmationQuestion}
                        </Typography>
                      </Box>

                      {step.stepData.confirmationType === ConfirmationType.BOOLEAN ? (
                        <Box className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            checked={confirmationResponse === 'true'}
                            onChange={(e) => setConfirmationResponse(e.target.checked ? 'true' : 'false')}
                            className="mt-1"
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <Typography variant="body14" className="flex-1">
                            {step.stepData.confirmationQuestion}
                          </Typography>
                        </Box>
                      ) : (
                        <Box className="flex flex-col gap-2">
                          <input
                            type="text"
                            value={confirmationResponse}
                            onChange={(e) => setConfirmationResponse(e.target.value)}
                            className="p-2 border border-solid border-primaryIcy rounded"
                            style={{ width: '100%' }}
                          />
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Action Buttons */}
                  <Box className="flex flex-col gap-3 w-full">
                    <Box className="flex flex-col lg:flex-row gap-2 w-full">
                      <WolfButton
                        onClick={() => {
                          if (onMarkStep) {
                            onMarkStep(index, StepExecutionStatus.FAILED, executionNotes);
                            setExecutionNotes('');
                            setConfirmationResponse('');
                          }
                        }}
                        variant="outlined"
                        color="error"
                        size="small"
                        className="flex-1"
                        startIcon={<ErrorOutline />}>
                        {localeProcesses['markStepWithError']}
                      </WolfButton>
                      <WolfButton
                        onClick={() => {
                          if (onMarkStep) {
                            onMarkStep(index, StepExecutionStatus.SKIPPED, executionNotes);
                            setExecutionNotes('');
                            setConfirmationResponse('');
                          }
                        }}
                        variant="outlined"
                        color="secondary"
                        size="small"
                        className="flex-1"
                        startIcon={<RedoOutlined />}>
                        {localeProcesses['markStepNotDone']}
                      </WolfButton>
                      <WolfButton
                        onClick={() => {
                          if (onMarkStep) {
                            onMarkStep(index, StepExecutionStatus.COMPLETED, executionNotes, confirmationResponse);
                            setExecutionNotes('');
                            setConfirmationResponse('');
                          }
                        }}
                        variant="contained"
                        color="success"
                        size="small"
                        className="flex-1"
                        startIcon={<CheckCircleOutline />}>
                        {localeProcesses['markStepDone']}
                      </WolfButton>
                    </Box>
                    {currentStepIndex > 0 && (
                      <Box className="flex justify-start">
                        <WolfButton
                          onClick={() => {
                            if (onGoBack) {
                              onGoBack();
                            }
                          }}
                          variant="outlined"
                          color="secondary"
                          size="small"
                          disabled={isGoingBack}
                          startIcon={<ArrowBack />}>
                          {localeProcesses['backToPreviousStep']}
                        </WolfButton>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {!isCurrentStep && stepExecution && (
                <Box className="flex items-center gap-2">
                  {stepStatus === StepExecutionStatus.COMPLETED && (
                    <>
                      <CheckCircleOutline sx={{ color: 'success.main', fontSize: 20 }} />
                      <Typography variant="body14" className="text-feedbackSuccess">{localeProcesses['stepDone']}</Typography>
                    </>
                  )}
                  {stepStatus === StepExecutionStatus.FAILED && (
                    <>
                      <ErrorOutline sx={{ color: 'error.main', fontSize: 20 }} />
                      <Typography variant="body14" className="text-feedbackError">{localeProcesses['stepWithError']}</Typography>
                    </>
                  )}
                  {stepStatus === StepExecutionStatus.SKIPPED && (
                    <>
                      <RedoOutlined sx={{ color: 'warning.main', fontSize: 20 }} />
                      <Typography variant="body14" className="text-feedbackWarning">{localeProcesses['stepSkipped']}</Typography>
                    </>
                  )}
                  {stepStatus === StepExecutionStatus.PENDING && (
                    <>
                      <PendingOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="body14" className="text-gray-600">{localeProcesses['stepPending']}</Typography>
                    </>
                  )}
                </Box>
              )}
              {!isCurrentStep && !stepExecution && (
                <Box className="flex items-center gap-2">
                  <PendingOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="body14" className="text-gray-600">{localeProcesses['stepPending']}</Typography>
                </Box>
              )}
            </Box>
            );
          })()}
        </Box>
      ),
  );

  return (
    <Box className={`flex flex-wrap p-4 max-w-screen-md mx-auto ${!hasVersions ? 'mb-28' : ''}`}>
      {/* Execution Note - shown above first step when executing */}
      {isExecuting && executionNote && (
        <Box className="w-full mb-4 p-4 bg-primaryIcy rounded-lg border border-solid border-primary">
          <Typography variant="body12semibold" className="text-primary mb-1">
            {localeProcesses['executionNote']}
          </Typography>
          <Typography variant="body14" className="whitespace-pre-wrap">
            {executionNote}
          </Typography>
        </Box>
      )}
      {stepList}

      {/* TODO: Separate component */}
      <Dialog
        className="flex justify-center items-center"
        open={showImageModal}
        onClose={closeModal}
        maxWidth="xl"
        closeAfterTransition>
        <Fade in={showImageModal}>
          <div className="bg-white m-10">
            <div className="flex justify-between items-center mb-5">
              <Button variant="contained" color="warning" onClick={closeModal}>
                X
              </Button>
            </div>
            <div>
              <img src={imgSrc} alt="expanded in modal" className="max-h-fit max-w-fit" />
              <div className="flex justify-between items-center">
                <Button variant="contained" color="warning" onClick={closeModal}>
                  {localeCommon['close']}
                </Button>
              </div>
            </div>
          </div>
        </Fade>
      </Dialog>
    </Box>
  );
};

export default ProcessContentStatic;
